import type {Repository} from '@github-ui/current-repository'
import {activityIndexPath} from '@github-ui/paths'
import {SafeHTMLBox} from '@github-ui/safe-html'
import {Box, BranchName, Label, Truncate} from '@primer/react'

import {Ago} from '../../react-shared/Ago'
import {useBaseUrl} from '../contexts/BaseUrlContext'
import {usePerPage} from '../contexts/PerPageContext'
import type {Commit, Push} from '../types/activity-types'
import {ActivityOverflowMenu} from './ActivityOverflowMenu'
import {Pusher} from './Pusher'

const refPrefix = 'refs/heads/'
const tagPrefix = 'refs/tags/'

export function ActivityCard({
  push,
  repo,
  showRefInfo,
  showFullShas,
}: {
  push: Push
  repo: Repository
  showRefInfo: boolean
  showFullShas: boolean
}) {
  return (
    <Box
      sx={{
        borderTopColor: 'border.default',
        borderTopStyle: 'solid',
        borderTopWidth: 1,
        '&:first-child': {borderTopWidth: 0},
        padding: 2,
        paddingX: 3,
        display: 'flex',
        justifyContent: 'space-between',
        '&:hover': {backgroundColor: 'canvas.subtle'},
      }}
    >
      <Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 1}}>
          <h2 data-testid="push-link" className="h5">
            {activityActionMessage(push)}
          </h2>
          <Box sx={{display: 'flex', alignSelf: 'flex-start', gap: 2}} data-testid="push-labels">
            {activityLabels(push)}
          </Box>
        </Box>
        <Box sx={{color: 'fg.muted', fontSize: 0}}>
          {push.pusher && <Pusher pusher={push.pusher} />}
          <ActionText push={push} repo={repo} showRefInfo={showRefInfo} />
          {showFullShas ? (
            <>
              <ActivityDate push={push} />
              <FullShaCompareText push={push} />
            </>
          ) : (
            <>
              <CompareText push={push} />
              <ActivityDate push={push} />
            </>
          )}
        </Box>
      </Box>
      <Box sx={{display: ['block', 'none'], alignSelf: 'flex-start'}}>
        <ActivityOverflowMenu push={push} />
      </Box>
      <Box sx={{display: ['none', 'block']}}>
        <ActivityOverflowMenu push={push} />
      </Box>
    </Box>
  )
}

function activityLabels(push: Push) {
  const labels: JSX.Element[] = []

  if (push.pushType === 'force_push') {
    labels.push(
      <Label key="force-push" variant="attention">
        Force push
      </Label>,
    )
  }

  if (push.pushType === 'pr_merge') {
    labels.push(
      <Label key="pr-merge" variant="done">
        Pull request merge
      </Label>,
    )
  }

  if (push.after && !push.commit) {
    labels.push(
      <Label key="missing-commit" variant="severe">
        Missing commit
      </Label>,
    )
  }

  return labels
}

function activityActionMessage(push: Push) {
  if (push.pushType === 'merge_queue_merge') {
    return 'Merge queue merge'
  } else if (push.pushType === 'branch_deletion') {
    if (push.ref.startsWith(tagPrefix)) {
      return 'Deleted tag'
    } else {
      return 'Deleted branch'
    }
  } else if (push.commit?.message) {
    return pushCommitMessage({commit: push.commit})
  } else if (push.pushType === 'branch_creation') {
    return 'Created branch'
  } else {
    return 'Updated branch'
  }
}

function ActivityDate({push}: {push: Push}) {
  const actionDate = push.pushedAt
  return actionDate ? (
    <span data-testid="push-date" style={{display: 'inline-block'}}>
      &nbsp;&bull;&nbsp;
      <Ago timestamp={new Date(actionDate)} />
    </span>
  ) : null
}

function ActionText({push, repo, showRefInfo}: {push: Push; repo: Repository; showRefInfo: boolean}) {
  const baseUrl = useBaseUrl()
  const perPage = usePerPage()
  const refName = push.ref.startsWith(refPrefix) ? push.ref.substring(refPrefix.length) : push.ref
  let action = ''
  let message = ''

  if (push.aheadBehind && push.aheadBehind.length > 0) {
    const [ahead, behind] = push.aheadBehind

    if (ahead !== 0 && behind === 0) {
      message = `adding ${ahead} ${pluralize(ahead!)}`
    } else if (ahead === 0 && behind !== 0) {
      message = `removing ${behind} ${pluralize(behind!)}`
    } else {
      message = `removing ${behind} ${pluralize(behind!)} and adding ${ahead} ${pluralize(ahead!)}`
    }
  }

  if (push.pushType === 'branch_deletion') {
    const isTag = push.ref.startsWith(tagPrefix)
    action = showRefInfo ? 'deleted' : `deleted this ${isTag ? 'tag' : 'branch'}`
  } else if (push.pushType === 'branch_creation') {
    action = showRefInfo ? 'created' : 'created this branch'
  } else if (push.pushType === 'force_push') {
    action = showRefInfo ? 'force pushed to' : 'force pushed'
  } else if (!message && push.commitsCount != null) {
    // skip this case if we have ahead/behind information
    action = showRefInfo
      ? `pushed ${push.commitsCount} ${pluralize(push.commitsCount)} to`
      : `pushed ${push.commitsCount} ${pluralize(push.commitsCount)}`
  } else {
    action = showRefInfo ? 'pushed to' : 'pushed'
  }

  return (
    <span data-testid="action-text">
      {action}
      {showRefInfo && (
        <>
          {' '}
          <BranchName href={activityIndexPath({repo, baseUrl, branch: refName, perPage})}>{refName}</BranchName>
        </>
      )}
      {message && <> {message}</>}
    </span>
  )
}

function pluralize(count: number) {
  return count === 1 ? 'commit' : 'commits'
}

function pushCommitMessage({commit}: {commit: Commit}) {
  return commit.shortMessageHtmlLink ? (
    <Truncate title={commit.message} sx={{maxWidth: '100%'}}>
      <SafeHTMLBox
        sx={{
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
        html={commit.shortMessageHtmlLink}
      />
    </Truncate>
  ) : (
    // Edge case, should never happen (in theory)
    // But if it happens we should at least show something
    commit.message
  )
}

function CompareText({push}: {push: Push}) {
  let compare = ''

  if (push.before && push.after) {
    compare = `${shortSha(push.before)}…${shortSha(push.after)}` // push
  } else if (push.before && !push.after) {
    compare = '' // deleted - Leaving this empty case here for completeness
  } else if (!push.before && push.after) {
    compare = `${shortSha(push.after)}` // created
  }

  return compare ? (
    <span data-testid="compare-text">
      &nbsp;&bull;&nbsp;
      {compare}
    </span>
  ) : null
}

function FullShaCompareText({push}: {push: Push}) {
  const emptySha = '0000000000000000000000000000000000000000'

  return (
    <Box data-testid="compare-text">
      {push.before || emptySha}&nbsp;&rarr;&nbsp;{push.after || emptySha}
    </Box>
  )
}

function shortSha(sha: string) {
  return sha.slice(0, 7)
}

try{ ActivityCard.displayName ||= 'ActivityCard' } catch {}
try{ ActivityDate.displayName ||= 'ActivityDate' } catch {}
try{ ActionText.displayName ||= 'ActionText' } catch {}
try{ CompareText.displayName ||= 'CompareText' } catch {}
try{ FullShaCompareText.displayName ||= 'FullShaCompareText' } catch {}